/**
 * Part5 切替デモ（DEV用・フロント完結）。
 *
 * Part5「ONA具体化」と「ネームジェネレータ」を切替表示して見比べるデモ。
 * 実フロー(DesignQuestionnaireFlow)は getPart5Mode() で同じ分岐を行う。
 * ここではダミー同僚＋トグルで両モードを確認できる。
 */
import React, { useState } from 'react';
import { Box, Paper, Typography, Stack } from '@mui/material';
import { ChoiceQuestionUI } from './ChoiceQuestionUI';
import { NameGeneratorUI } from './NameGeneratorUI';
import type { Part5Mode } from '../config/part5Mode';

const DUMMY_COLLEAGUES = [
  { id: 'c1', name: '佐藤' },
  { id: 'c2', name: '鈴木' },
  { id: 'c3', name: '高橋' },
  { id: 'c4', name: '田中' },
  { id: 'c5', name: '渡辺' },
  { id: 'c6', name: '伊藤' },
];

const ONA_CHOICES = [
  { id: 'o0', text: '0人' },
  { id: 'o1', text: '1人' },
  { id: 'o2', text: '2〜3人' },
  { id: 'o3', text: '4人以上' },
];

export const Part5DemoPage: React.FC = () => {
  const [mode, setMode] = useState<Part5Mode>('namegen');
  const [choiceId, setChoiceId] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', px: 2, py: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Part5 入力モード切替デモ
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          回答画面のPart5を「ONA具体化」と「ネームジェネレータ（所属部署内の同僚を最大3名）」で切り替えられます。
          実フローも同じ <code>getPart5Mode()</code> で分岐します（既定 ona・会社単位設定の想定）。
        </Typography>
        <Stack direction="row" spacing={1}>
          {(['ona', 'namegen'] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                style={{
                  padding: '8px 14px',
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? '#fff' : '#5a6470',
                  background: active ? '#1565C0' : '#EEF1F4',
                  border: active ? '1.5px solid #1565C0' : '1.5px solid #D5DCE3',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                {m === 'ona' ? 'ONA具体化（現行）' : 'ネームジェネレータ（D）'}
              </button>
            );
          })}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        {/* gb74回答カード相当の枠 */}
        {mode === 'ona' ? (
          <ChoiceQuestionUI
            questionText="先月、仕事のことで繰り返し相談した相手は何人いましたか？"
            choices={ONA_CHOICES}
            selectedChoiceId={choiceId}
            onAnswer={(id) => setChoiceId(id)}
            onBack={() => {}}
            canGoBack={false}
          />
        ) : (
          <NameGeneratorUI
            questionText="仕事のことでよく相談する同僚を選んでください。"
            colleagues={DUMMY_COLLEAGUES}
            selectedIds={selectedIds}
            maxSelect={3}
            onChange={setSelectedIds}
            onNext={() => {}}
            onBack={() => {}}
            canGoBack={false}
          />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#FAFBFC' }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          収集される回答（デバッグ表示）
        </Typography>
        <Stack spacing={0.5}>
          {mode === 'ona' ? (
            <Typography variant="body2">
              選択: {ONA_CHOICES.find((c) => c.id === choiceId)?.text ?? '（未選択）'}
            </Typography>
          ) : (
            <Typography variant="body2">
              選択した同僚:{' '}
              {selectedIds.length
                ? selectedIds
                    .map((id) => DUMMY_COLLEAGUES.find((c) => c.id === id)?.name)
                    .filter(Boolean)
                    .join('・')
                : '（未選択）'}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default Part5DemoPage;
